import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';

import { getQuestionsValid } from 'app/store/data/formsConfig';
import { RootState } from 'app/store/rootReducer';
import { FieldsEnum, FormsEnum, TerminationReasonEnum } from 'app/shared/enums';
import { useForm } from 'app/shared/hooks/useForm';
import { TerminationFormInterface } from 'app/shared/interfaces/TerminationForm.interface';
import { useSetupTerminationPreviewData } from './useSetupTerminationPreviewData';

const formName = FormsEnum.TerminationGeneratorForm;

const firstSectionFields = [
  FieldsEnum.CarMake,
  FieldsEnum.CarModel,
  FieldsEnum.CarRegistrationNumber,
];

const secondSectionFields = [
  FieldsEnum.TerminationName,
  FieldsEnum.TerminationLastName,
  FieldsEnum.TerminationZipCode,
  FieldsEnum.TerminationCity,
  FieldsEnum.TerminationStreetName,
  FieldsEnum.TerminationStreetNumber,
];

const thirdSectionFields = [
  FieldsEnum.TerminationReason,
  FieldsEnum.TerminationPolicyNumber,
  FieldsEnum.TerminationPreviousInsurerName,
];

export function useTerminationPreviewDocument() {
  const [isFirstSectionFilled, setIsFirstSectionFilled] = useState(false);
  const [isSecondSectionFilled, setIsSecondSectionFilled] = useState(false);
  const [isThirdSectionFilled, setIsThirdSectionFilled] = useState(false);
  const { formValues } = useForm<TerminationFormInterface>(formName);
  const data = useSetupTerminationPreviewData(formValues);

  const isSectionFilled = (questions: FieldsEnum[]) =>
    questions.every((question: FieldsEnum) => {
      const isQuestionFilled = get(formValues, question);

      return !isNil(isQuestionFilled);
    });

  const isFirstSectionValid = useSelector((state: RootState) =>
    getQuestionsValid({
      state,
      formName,
      questionKeys: firstSectionFields,
    })
  );

  const isSecondSectionValid = useSelector((state: RootState) =>
    getQuestionsValid({
      state,
      formName,
      questionKeys: secondSectionFields,
    })
  );

  const isThirdSectionValid = useSelector((state: RootState) => {
    const isTerminationReasonArticle31 =
      formValues?.noticeReason === TerminationReasonEnum.Article31;

    return getQuestionsValid({
      state,
      formName,
      questionKeys: isTerminationReasonArticle31
        ? [...thirdSectionFields, FieldsEnum.TerminationNoticeDate]
        : thirdSectionFields,
    });
  });

  useEffect(() => {
    setIsFirstSectionFilled(
      isSectionFilled(firstSectionFields) && isFirstSectionValid
    );

    setIsSecondSectionFilled(
      isSectionFilled(secondSectionFields) && isSecondSectionValid
    );

    const isTerminationReasonArticle31 =
      formValues?.noticeReason === TerminationReasonEnum.Article31;

    setIsThirdSectionFilled(
      isSectionFilled(
        isTerminationReasonArticle31
          ? [...thirdSectionFields, FieldsEnum.TerminationNoticeDate]
          : thirdSectionFields
      ) && isThirdSectionValid
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  return {
    data,
    isFirstSectionFilled,
    isSecondSectionFilled,
    isThirdSectionFilled,
  };
}
